import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "@/services/firebase/init";
import { WorkspaceError, workspaceRequest } from "@/services/firebase/artistWorkspace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminEmptyState, AdminErrorAlert, AdminStatusBadge } from "@/components/admin-ui";
import { useArtistConfirm } from "./ArtistConfirm";

interface Member {
  memberUid: string;
  role: string;
  active: boolean;
}
interface LookedUp {
  uid: string;
  displayName: string | null;
  photoURL: string | null;
}
const ROLE_LABELS: Record<string, string> = { artist: "藝人", editor: "代管編輯", reviewer: "審核者" };

/**
 * O3 成員與開通: email → uid lookup, one-click 加入為藝人 (with self-publish), role grants and revocation.
 * Same backend calls the workspace dialog used: workspaceMembers / workspaceLookupUser / workspaceSetMember (staff only).
 */
export default function ArtistMembersPanel({ artistId }: { artistId: string }) {
  const client = useQueryClient();
  const [members, setMembers] = useState<Member[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookedUp, setLookedUp] = useState<LookedUp | null>(null);
  const [memberUid, setMemberUid] = useState("");
  const [memberRole, setMemberRole] = useState("editor");
  const generation = useRef(0);
  const locked = useRef(false);
  const { confirm, dialog } = useArtistConfirm();

  async function run(work: (epoch: number) => Promise<void>) {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    const epoch = generation.current;
    try {
      await work(epoch);
    } catch (e) {
      if (epoch === generation.current) {
        if (e instanceof WorkspaceError && ["FORBIDDEN", "UNAUTHENTICATED"].includes(e.code)) setMembers([]);
        setError(e instanceof Error ? e.message : "操作未完成");
      }
    } finally {
      if (epoch === generation.current) {
        locked.current = false;
        setBusy(false);
      }
    }
  }
  async function loadMembers(epoch: number) {
    const result = await workspaceRequest<{ items: Member[] }>("workspaceMembers", artistId);
    if (epoch !== generation.current) return;
    setMembers(result.items);
    setLoaded(true);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      generation.current++;
      locked.current = false;
      setBusy(false);
      setMembers([]);
      setLoaded(false);
      setLookedUp(null);
      setError("");
      setNotice("");
      if (user) void run(loadMembers);
    });
    return () => {
      generation.current++;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistId]);

  function changeMember(uid: string, roleName: string, active: boolean, canPublish?: boolean) {
    void run(async (epoch) => {
      const result = await workspaceRequest<{ activated?: boolean; pushed?: boolean }>("workspaceSetMember", artistId, {
        memberUid: uid,
        role: roleName,
        active,
        ...(canPublish === undefined ? {} : { canPublish }),
      });
      await loadMembers(epoch);
      // Owners drive verification (U6 overview) — refresh it.
      void client.invalidateQueries({ queryKey: ["artist-admin"] });
      if (epoch === generation.current)
        setNotice(
          !active
            ? "已撤銷此藝人的工作區權限（不發推播）。"
            : result.activated
              ? result.pushed
                ? "已開通創作者專頁，並已推播「你的 Reel+ 創作者專頁已開通」。"
                : "已開通創作者專頁；此帳號沒有推播 token，未發推播。"
              : "藝人工作區授權已更新。",
        );
    });
  }
  function lookupUser() {
    void run(async (epoch) => {
      setLookedUp(null);
      const found = await workspaceRequest<LookedUp>("workspaceLookupUser", artistId, { email: lookupEmail.trim() });
      if (epoch !== generation.current) return;
      setLookedUp(found);
      setMemberUid(found.uid);
      setNotice("已找到帳號，可一鍵加入為藝人（含自行發布權）。");
    });
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>成員與開通</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">
          限定此藝人；不授予整個 CMS 管理權限。輸入藝人登入 App 的 Email 查到 UID 後一鍵開通，或直接輸入既有使用者
          UID。藝人／編輯可編修及送審，審核者可讀取與退回。首次開通為藝人時會推播「你的 Reel+ 創作者專頁已開通」；撤權不推播。
        </p>
        <p className="text-sm text-muted-foreground">
          也可由「
          <Link to="/creators/applications" className="underline underline-offset-2">
            創作者申請
          </Link>
          」佇列核准後自動建立成員。
        </p>
        {error && <AdminErrorAlert message={error} onDismiss={() => setError("")} />}
        {notice && <p role="status">{notice}</p>}
        {busy && <p role="status">處理中…</p>}

        <section className="space-y-2" aria-label="用 Email 開通藝人">
          <h3 className="font-medium">用 Email 開通</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="藝人 Email"
              type="email"
              placeholder="artist@example.com"
              className="max-w-sm"
              value={lookupEmail}
              disabled={busy}
              onChange={(e) => setLookupEmail(e.target.value)}
            />
            <Button variant="outline" disabled={busy || !lookupEmail.trim()} onClick={lookupUser}>
              用 Email 查 UID
            </Button>
          </div>
          {lookedUp && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {lookedUp.photoURL && <img src={lookedUp.photoURL} alt="" className="h-6 w-6 rounded-full object-cover" />}
              <span className="break-all">
                {lookedUp.displayName || "（未設定名稱）"} · {lookedUp.uid}
              </span>
              <Button disabled={busy} onClick={() => changeMember(lookedUp.uid, "artist", true, true)}>
                加入為藝人
              </Button>
            </div>
          )}
        </section>

        <section className="space-y-2" aria-label="以 UID 授權">
          <h3 className="font-medium">以 UID 授權</h3>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label="授權使用者 UID"
              className="max-w-sm"
              value={memberUid}
              disabled={busy}
              onChange={(e) => setMemberUid(e.target.value)}
            />
            <select
              aria-label="工作區角色"
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={memberRole}
              disabled={busy}
              onChange={(e) => setMemberRole(e.target.value)}
            >
              <option value="artist">藝人</option>
              <option value="editor">代管編輯</option>
              <option value="reviewer">審核者</option>
            </select>
            <Button disabled={busy || !memberUid.trim()} onClick={() => changeMember(memberUid.trim(), memberRole, true)}>
              授權
            </Button>
          </div>
        </section>

        <section className="space-y-2" aria-label="成員名單">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-medium">成員名單（前 100 筆）</h3>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => void run(loadMembers)}>
              重新整理
            </Button>
          </div>
          {loaded && members.length === 0 && <AdminEmptyState title="尚無成員" description="以 Email 或 UID 開通後會出現在這裡。" />}
          {members.length > 0 && (
            <ul className="divide-y rounded-md border">
              {members.map((member) => (
                <li key={member.memberUid} className="flex flex-wrap items-center gap-3 p-3">
                  <span className="min-w-0 flex-1 break-all text-sm">{member.memberUid}</span>
                  <AdminStatusBadge label={ROLE_LABELS[member.role] ?? member.role} tone={member.role === "artist" ? "purple" : "neutral"} />
                  <AdminStatusBadge label={member.active ? "有效" : "已撤銷"} tone={member.active ? "success" : "neutral"} />
                  {member.active && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        confirm({
                          title: "撤銷成員權限",
                          message:
                            member.role === "artist"
                              ? "撤銷後此帳號不再是此藝人的擁有者，無法管理專頁；若沒有其他有效藝人擁有者，App 上的認證徽章會消失。不發推播。"
                              : "撤銷後此帳號無法再編修或審核此藝人的內容。不發推播。",
                          lines: [`UID：${member.memberUid}`, `角色：${ROLE_LABELS[member.role] ?? member.role}`],
                          confirmText: "撤銷",
                          danger: true,
                          onConfirm: () => changeMember(member.memberUid, member.role, false),
                        })
                      }
                    >
                      撤銷
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
        {dialog}
      </CardContent>
    </Card>
  );
}
